import React, { useState, useEffect, useRef } from 'react';

/* ═══════════════════════════════════════════
   GRAND GATHER REGISTRY — APP SHOWCASE
   Realistic app screen mockups for competition
   ═══════════════════════════════════════════ */

const C = {
  coral: '#E3425C',
  coralDark: '#D13A4F',
  charcoal: '#0F0F11',
  charcoalMid: '#1A1A1E',
  gold: '#C9A84C',
  cream: '#FAF8F5',
  sage: '#5C7A5A',
};

function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function Reveal({ children, delay = 0, direction = 'up' }) {
  const [ref, inView] = useInView();
  const transforms = { up: 'translateY(40px)', down: 'translateY(-40px)', left: 'translateX(40px)', right: 'translateX(-40px)', scale: 'scale(0.92)' };
  return (
    <div ref={ref} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? 'none' : transforms[direction],
      transition: `opacity 0.9s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.9s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
    }}>
      {children}
    </div>
  );
}

/* Phone mockup */
function Phone({ children, style = {}, label }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', ...style }}>
      <div style={{
        width: '300px', height: '620px',
        borderRadius: '40px',
        background: '#000',
        padding: '12px',
        boxShadow: '0 30px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.06), inset 0 0 0 1px rgba(255,255,255,0.05)',
        position: 'relative', flexShrink: 0,
      }}>
        {/* Notch */}
        <div style={{
          position: 'absolute', top: '12px', left: '50%', transform: 'translateX(-50%)',
          width: '100px', height: '28px', background: '#000', borderRadius: '0 0 16px 16px', zIndex: 10,
        }}>
          <div style={{ position: 'absolute', top: '8px', left: '50%', transform: 'translateX(-50%)', width: '8px', height: '8px', borderRadius: '50%', background: '#1a1a2e' }} />
        </div>
        {/* Screen */}
        <div style={{
          width: '100%', height: '100%', borderRadius: '28px', overflow: 'hidden',
          background: '#fff', position: 'relative',
        }}>
          {children}
        </div>
        {/* Home indicator */}
        <div style={{
          position: 'absolute', bottom: '16px', left: '50%', transform: 'translateX(-50%)',
          width: '100px', height: '4px', borderRadius: '4px', background: 'rgba(255,255,255,0.2)',
        }} />
      </div>
      {label && (
        <div style={{
          fontSize: '14px', fontWeight: 700, color: 'rgba(255,255,255,0.7)',
          fontFamily: "'Instrument Sans', sans-serif",
          letterSpacing: '0.5px',
        }}>{label}</div>
      )}
    </div>
  );
}

/* ── SCREEN MOCKUPS ── */

function DashboardScreen() {
  return (
    <div style={{ height: '100%', background: '#FAFAFA', fontFamily: "'Instrument Sans', sans-serif", fontSize: '12px' }}>
      {/* Status bar */}
      <div style={{ padding: '36px 16px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '10px', color: '#999' }}>
        <span>9:41</span>
        <div style={{ display: 'flex', gap: '4px' }}>
          <div style={{ width: '14px', height: '8px', border: '1px solid #999', borderRadius: '2px', position: 'relative' }}>
            <div style={{ position: 'absolute', inset: '1px', background: '#4CAF50', borderRadius: '1px', width: '70%' }} />
          </div>
        </div>
      </div>

      {/* Header */}
      <div style={{ padding: '8px 16px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
          <div>
            <div style={{ fontSize: '10px', color: '#999', fontWeight: 500 }}>Welcome back</div>
            <div style={{ fontSize: '18px', fontWeight: 700, color: '#1a1a1a' }}>Thabo M.</div>
          </div>
          <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: `linear-gradient(135deg, ${C.coral}, ${C.gold})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: '14px' }}>T</div>
        </div>
      </div>

      {/* Quick Stats */}
      <div style={{ padding: '0 16px 12px', display: 'flex', gap: '8px' }}>
        {[
          { n: '3', l: 'Events', c: C.coral },
          { n: '47', l: 'Guests', c: '#3B82F6' },
          { n: '12', l: 'RSVPs', c: '#10B981' },
        ].map((s, i) => (
          <div key={i} style={{ flex: 1, padding: '10px', borderRadius: '12px', background: 'white', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: 800, color: s.c }}>{s.n}</div>
            <div style={{ fontSize: '9px', color: '#999', fontWeight: 600, marginTop: '2px' }}>{s.l}</div>
          </div>
        ))}
      </div>

      {/* Event Cards */}
      <div style={{ padding: '0 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <span style={{ fontSize: '13px', fontWeight: 700, color: '#333' }}>My Events</span>
          <span style={{ fontSize: '10px', color: C.coral, fontWeight: 600 }}>+ New Event</span>
        </div>

        {/* Wedding card */}
        <div style={{ borderRadius: '14px', overflow: 'hidden', marginBottom: '10px', background: 'white', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <div style={{ height: '80px', background: 'linear-gradient(135deg, #E8A0BF 0%, #D4739D 100%)', position: 'relative', padding: '12px' }}>
            <div style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(255,255,255,0.9)', borderRadius: '20px', padding: '3px 10px', fontSize: '8px', fontWeight: 700, color: '#D4739D' }}>APR 15</div>
            <div style={{ position: 'absolute', bottom: '10px', left: '12px' }}>
              <div style={{ fontSize: '14px', fontWeight: 800, color: 'white' }}>Thabo & Lerato Wedding</div>
              <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.8)' }}>Sandton Convention Centre</div>
            </div>
          </div>
          <div style={{ padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ textAlign: 'center' }}><div style={{ fontSize: '12px', fontWeight: 800, color: '#333' }}>120</div><div style={{ fontSize: '8px', color: '#999' }}>Guests</div></div>
              <div style={{ textAlign: 'center' }}><div style={{ fontSize: '12px', fontWeight: 800, color: '#10B981' }}>85</div><div style={{ fontSize: '8px', color: '#999' }}>RSVPs</div></div>
              <div style={{ textAlign: 'center' }}><div style={{ fontSize: '12px', fontWeight: 800, color: C.gold }}>R24k</div><div style={{ fontSize: '8px', color: '#999' }}>Gifts</div></div>
            </div>
            <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: `${C.coral}15`, color: C.coral, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>→</div>
          </div>
        </div>

        {/* Lobola card */}
        <div style={{ borderRadius: '14px', overflow: 'hidden', marginBottom: '10px', background: 'white', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <div style={{ height: '80px', background: 'linear-gradient(135deg, #D4A574 0%, #8B6914 100%)', position: 'relative', padding: '12px' }}>
            <div style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(255,255,255,0.9)', borderRadius: '20px', padding: '3px 10px', fontSize: '8px', fontWeight: 700, color: '#8B6914' }}>MAY 22</div>
            <div style={{ position: 'absolute', bottom: '10px', left: '12px' }}>
              <div style={{ fontSize: '14px', fontWeight: 800, color: 'white' }}>Lobola Ceremony</div>
              <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.8)' }}>Pretoria East</div>
            </div>
          </div>
          <div style={{ padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ textAlign: 'center' }}><div style={{ fontSize: '12px', fontWeight: 800, color: '#333' }}>45</div><div style={{ fontSize: '8px', color: '#999' }}>Guests</div></div>
              <div style={{ textAlign: 'center' }}><div style={{ fontSize: '12px', fontWeight: 800, color: '#10B981' }}>32</div><div style={{ fontSize: '8px', color: '#999' }}>RSVPs</div></div>
            </div>
            <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#D4A57415', color: '#D4A574', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>→</div>
          </div>
        </div>

        {/* Birthday card */}
        <div style={{ borderRadius: '14px', overflow: 'hidden', background: 'white', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <div style={{ height: '60px', background: 'linear-gradient(135deg, #FFB347 0%, #FF6B6B 100%)', position: 'relative', padding: '12px' }}>
            <div style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(255,255,255,0.9)', borderRadius: '20px', padding: '3px 10px', fontSize: '8px', fontWeight: 700, color: '#FF6B6B' }}>JUN 8</div>
            <div style={{ position: 'absolute', bottom: '10px', left: '12px' }}>
              <div style={{ fontSize: '13px', fontWeight: 800, color: 'white' }}>Nomsa's 30th Birthday</div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom nav */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '10px 20px 24px', background: 'white', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-around' }}>
        {['🏠', '📋', '🔔', '👤'].map((icon, i) => (
          <div key={i} style={{ textAlign: 'center', opacity: i === 0 ? 1 : 0.4 }}>
            <div style={{ fontSize: '18px' }}>{icon}</div>
            <div style={{ fontSize: '8px', color: i === 0 ? C.coral : '#999', fontWeight: 600, marginTop: '2px' }}>
              {['Home', 'Events', 'Alerts', 'Profile'][i]}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EventWorkspaceScreen() {
  return (
    <div style={{ height: '100%', background: '#fff', fontFamily: "'Instrument Sans', sans-serif", fontSize: '12px' }}>
      {/* Header */}
      <div style={{ padding: '36px 14px 0', background: 'linear-gradient(135deg, #E8A0BF 0%, #D4739D 100%)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <span style={{ color: 'white', fontSize: '16px' }}>←</span>
          <span style={{ flex: 1, fontSize: '15px', fontWeight: 700, color: 'white' }}>Thabo & Lerato Wedding</span>
          <span style={{ fontSize: '16px' }}>⋯</span>
        </div>
        <div style={{ display: 'flex', gap: '4px', paddingBottom: '0' }}>
          {['Details', 'Guests', 'Invites', 'Gifts', 'Wall'].map((tab, i) => (
            <div key={i} style={{
              padding: '8px 10px', fontSize: '10px', fontWeight: i === 0 ? 700 : 500,
              color: i === 0 ? 'white' : 'rgba(255,255,255,0.6)',
              borderBottom: i === 0 ? '2px solid white' : '2px solid transparent',
            }}>{tab}</div>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '14px' }}>
        {/* Event info card */}
        <div style={{ padding: '14px', borderRadius: '12px', background: '#fafafa', marginBottom: '12px' }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '10px' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'linear-gradient(135deg, #E8A0BF, #D4739D)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px' }}>💍</div>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#1a1a1a' }}>Wedding</div>
              <div style={{ fontSize: '10px', color: '#999' }}>April 15, 2026 • 14:00</div>
              <div style={{ fontSize: '10px', color: '#999' }}>Sandton Convention Centre</div>
            </div>
          </div>
          <p style={{ fontSize: '11px', color: '#666', lineHeight: 1.6 }}>
            Join us as Thabo and Lerato celebrate their union. A beautiful day of love, family, and tradition.
          </p>
        </div>

        {/* Budget overview */}
        <div style={{ padding: '12px', borderRadius: '12px', background: '#fafafa', marginBottom: '12px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#333', marginBottom: '8px' }}>Budget Overview</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ fontSize: '10px', color: '#999' }}>Total Budget</span>
            <span style={{ fontSize: '12px', fontWeight: 800, color: '#333' }}>R 150,000</span>
          </div>
          <div style={{ height: '6px', background: '#eee', borderRadius: '3px', overflow: 'hidden', marginBottom: '4px' }}>
            <div style={{ height: '100%', width: '62%', background: `linear-gradient(90deg, ${C.coral}, ${C.gold})`, borderRadius: '3px' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '9px', color: '#10B981' }}>R 93,000 spent</span>
            <span style={{ fontSize: '9px', color: '#999' }}>R 57,000 remaining</span>
          </div>
        </div>

        {/* Checklist preview */}
        <div style={{ padding: '12px', borderRadius: '12px', background: '#fafafa', marginBottom: '12px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#333', marginBottom: '8px' }}>Checklist</div>
          {[
            { t: 'Book venue', done: true },
            { t: 'Confirm caterer', done: true },
            { t: 'Send invitations', done: true },
            { t: 'Final dress fitting', done: false },
            { t: 'Wedding rehearsal', done: false },
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 0', borderBottom: i < 4 ? '1px solid #f0f0f0' : 'none' }}>
              <div style={{
                width: '16px', height: '16px', borderRadius: '4px',
                background: item.done ? '#10B981' : 'white',
                border: item.done ? 'none' : '1.5px solid #ddd',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'white', fontSize: '9px',
              }}>{item.done ? '✓' : ''}</div>
              <span style={{ fontSize: '11px', color: item.done ? '#999' : '#333', textDecoration: item.done ? 'line-through' : 'none' }}>{item.t}</span>
            </div>
          ))}
        </div>

        {/* Timeline */}
        <div style={{ padding: '12px', borderRadius: '12px', background: '#fafafa' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#333', marginBottom: '8px' }}>Day Programme</div>
          {[
            { time: '13:00', event: 'Guest arrival', color: '#3B82F6' },
            { time: '14:00', event: 'Ceremony begins', color: C.coral },
            { time: '15:30', event: 'Photo session', color: '#F59E0B' },
            { time: '17:00', event: 'Reception & dinner', color: '#10B981' },
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: '10px', marginBottom: '8px' }}>
              <div style={{ fontSize: '9px', fontWeight: 700, color: item.color, width: '36px', paddingTop: '1px' }}>{item.time}</div>
              <div style={{ width: '2px', background: item.color, borderRadius: '1px', minHeight: '16px' }} />
              <div style={{ fontSize: '11px', color: '#555' }}>{item.event}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function GuestListScreen() {
  return (
    <div style={{ height: '100%', background: '#fff', fontFamily: "'Instrument Sans', sans-serif", fontSize: '12px' }}>
      <div style={{ padding: '36px 14px 12px', background: 'linear-gradient(135deg, #E8A0BF 0%, #D4739D 100%)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <span style={{ color: 'white', fontSize: '16px' }}>←</span>
          <span style={{ flex: 1, fontSize: '15px', fontWeight: 700, color: 'white' }}>Guest List</span>
          <span style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '20px', padding: '4px 10px', fontSize: '10px', color: 'white', fontWeight: 600 }}>120 guests</span>
        </div>
        {/* RSVP summary */}
        <div style={{ display: 'flex', gap: '6px' }}>
          {[
            { n: 85, l: 'Attending', c: '#10B981', bg: 'rgba(16,185,129,0.15)' },
            { n: 15, l: 'Declined', c: '#EF4444', bg: 'rgba(239,68,68,0.15)' },
            { n: 20, l: 'Pending', c: '#F59E0B', bg: 'rgba(245,158,11,0.15)' },
          ].map((s, i) => (
            <div key={i} style={{ flex: 1, padding: '8px', borderRadius: '10px', background: s.bg, textAlign: 'center' }}>
              <div style={{ fontSize: '16px', fontWeight: 800, color: s.c }}>{s.n}</div>
              <div style={{ fontSize: '8px', color: s.c, fontWeight: 600, opacity: 0.8 }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Search */}
      <div style={{ padding: '10px 14px' }}>
        <div style={{ padding: '8px 12px', borderRadius: '10px', background: '#f5f5f5', fontSize: '11px', color: '#bbb', display: 'flex', alignItems: 'center', gap: '6px' }}>
          🔍 Search guests...
        </div>
      </div>

      {/* Guest list */}
      <div style={{ padding: '0 14px' }}>
        {[
          { name: 'Sipho Ndlovu', rsvp: 'Attending', table: 'Table 3', diet: 'Halal', avatar: 'S', color: '#3B82F6' },
          { name: 'Naledi Molefe', rsvp: 'Attending', table: 'Table 1', diet: '', avatar: 'N', color: '#8B5CF6' },
          { name: 'Bongani Zulu', rsvp: 'Attending', table: 'Table 5', diet: 'Vegetarian', avatar: 'B', color: '#10B981' },
          { name: 'Palesa Khumalo', rsvp: 'Pending', table: '—', diet: '', avatar: 'P', color: '#F59E0B' },
          { name: 'Tshepo Mokoena', rsvp: 'Attending', table: 'Table 2', diet: '', avatar: 'T', color: C.coral },
          { name: 'Zanele Dlamini', rsvp: 'Attending', table: 'Table 4', diet: 'Kosher', avatar: 'Z', color: '#EC4899' },
          { name: 'Mpho Mahlangu', rsvp: 'Declined', table: '—', diet: '', avatar: 'M', color: '#6B7280' },
          { name: 'Thandi Nkosi', rsvp: 'Attending', table: 'Table 1', diet: '', avatar: 'T', color: '#14B8A6' },
          { name: 'Kagiso Tau', rsvp: 'Attending', table: 'Table 6', diet: '', avatar: 'K', color: '#F97316' },
          { name: 'Lindiwe Sithole', rsvp: 'Pending', table: '—', diet: '', avatar: 'L', color: '#A855F7' },
        ].map((g, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            padding: '10px 0', borderBottom: '1px solid #f5f5f5',
          }}>
            <div style={{
              width: '34px', height: '34px', borderRadius: '50%',
              background: `${g.color}18`, color: g.color,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: '13px',
            }}>{g.avatar}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#1a1a1a' }}>{g.name}</div>
              <div style={{ fontSize: '9px', color: '#999' }}>{g.table}{g.diet ? ` • ${g.diet}` : ''}</div>
            </div>
            <div style={{
              padding: '3px 8px', borderRadius: '20px', fontSize: '9px', fontWeight: 700,
              background: g.rsvp === 'Attending' ? '#10B98115' : g.rsvp === 'Declined' ? '#EF444415' : '#F59E0B15',
              color: g.rsvp === 'Attending' ? '#10B981' : g.rsvp === 'Declined' ? '#EF4444' : '#F59E0B',
            }}>{g.rsvp}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GigiScreen() {
  return (
    <div style={{ height: '100%', background: '#f8f8fa', fontFamily: "'Instrument Sans', sans-serif", fontSize: '12px', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ padding: '36px 14px 12px', background: 'white', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '16px' }}>←</span>
          <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: `linear-gradient(135deg, ${C.coral}, #FF8A9E)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>🤖</div>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#1a1a1a' }}>GiGi AI</div>
            <div style={{ fontSize: '9px', color: '#10B981', fontWeight: 600 }}>Online • Speaks 7 SA languages</div>
          </div>
        </div>
      </div>

      {/* Chat */}
      <div style={{ flex: 1, padding: '14px', overflow: 'hidden' }}>
        {/* GiGi message */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
          <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: `linear-gradient(135deg, ${C.coral}, #FF8A9E)`, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px' }}>🤖</div>
          <div style={{ background: 'white', borderRadius: '14px 14px 14px 4px', padding: '10px 14px', maxWidth: '85%', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: '11px', color: '#333', lineHeight: 1.6 }}>
              Sawubona! 👋 I'm GiGi, your AI event planner. I can help in <strong>English, isiZulu, Setswana, Sesotho, Sepitori, isiXhosa</strong> and <strong>Afrikaans</strong>. How can I help with your wedding?
            </div>
          </div>
        </div>

        {/* User message */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '14px' }}>
          <div style={{ background: C.coral, borderRadius: '14px 14px 4px 14px', padding: '10px 14px', maxWidth: '80%' }}>
            <div style={{ fontSize: '11px', color: 'white', lineHeight: 1.6 }}>Ngicela ungenzele i-checklist ye-wedding yami nge-isiZulu</div>
          </div>
        </div>

        {/* GiGi response */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
          <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: `linear-gradient(135deg, ${C.coral}, #FF8A9E)`, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px' }}>🤖</div>
          <div style={{ background: 'white', borderRadius: '14px 14px 14px 4px', padding: '10px 14px', maxWidth: '85%', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: '11px', color: '#333', lineHeight: 1.7 }}>
              Yebo! Nansi i-checklist yakho ye-wedding: ✨<br/><br/>
              ✅ Buka indawo yomcimbi<br/>
              ✅ Khetha umpheki wokudla<br/>
              ✅ Thumela izimemo<br/>
              ✅ Buka umculi/DJ<br/>
              ✅ Khetha ingubo yomshado<br/>
              ✅ Hlela izithombe<br/><br/>
              <span style={{ color: C.coral, fontWeight: 600 }}>📋 Apply to checklist?</span>
            </div>
          </div>
        </div>

        {/* Quick actions */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
          {['📋 Generate checklist', '💰 Suggest budget', '📝 Write description', '💝 Write thank you'].map((a, i) => (
            <div key={i} style={{
              padding: '6px 12px', borderRadius: '20px', fontSize: '10px',
              background: 'white', border: `1px solid ${C.coral}30`, color: C.coral,
              fontWeight: 600, boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
            }}>{a}</div>
          ))}
        </div>
      </div>

      {/* Input */}
      <div style={{ padding: '10px 14px 28px', background: 'white', borderTop: '1px solid #f0f0f0' }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <div style={{ flex: 1, padding: '10px 14px', borderRadius: '24px', background: '#f5f5f5', fontSize: '11px', color: '#bbb' }}>Ask GiGi anything...</div>
          <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: C.coral, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '14px' }}>↑</div>
        </div>
      </div>
    </div>
  );
}

function InvitationScreen() {
  return (
    <div style={{ height: '100%', background: '#1a1020', fontFamily: "'Instrument Sans', sans-serif", display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px 16px' }}>
      {/* Elegant invitation card */}
      <div style={{
        width: '100%', maxWidth: '260px',
        background: 'linear-gradient(180deg, #2a1f3a 0%, #1a1020 100%)',
        borderRadius: '20px',
        border: '1px solid rgba(201,168,76,0.3)',
        padding: '28px 20px',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Gold corner accents */}
        <div style={{ position: 'absolute', top: 0, left: 0, width: '60px', height: '60px', borderTop: `2px solid ${C.gold}40`, borderLeft: `2px solid ${C.gold}40`, borderRadius: '20px 0 0 0' }} />
        <div style={{ position: 'absolute', top: 0, right: 0, width: '60px', height: '60px', borderTop: `2px solid ${C.gold}40`, borderRight: `2px solid ${C.gold}40`, borderRadius: '0 20px 0 0' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '60px', height: '60px', borderBottom: `2px solid ${C.gold}40`, borderLeft: `2px solid ${C.gold}40`, borderRadius: '0 0 0 20px' }} />
        <div style={{ position: 'absolute', bottom: 0, right: 0, width: '60px', height: '60px', borderBottom: `2px solid ${C.gold}40`, borderRight: `2px solid ${C.gold}40`, borderRadius: '0 0 20px 0' }} />

        {/* GGR Logo */}
        <img src="/gift-icon-v2.png" alt="GGR" style={{ height: '36px', marginBottom: '12px', filter: 'drop-shadow(0 2px 8px rgba(201,168,76,0.3))' }} />

        <div style={{ fontSize: '10px', color: `${C.gold}99`, letterSpacing: '4px', textTransform: 'uppercase', marginBottom: '16px' }}>You are invited</div>

        <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.4)', marginBottom: '8px' }}>to the wedding of</div>

        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '26px', fontWeight: 700, color: C.gold, lineHeight: 1.2, marginBottom: '4px' }}>Thabo</div>
        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.3)', fontStyle: 'italic', margin: '4px 0' }}>&</div>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '26px', fontWeight: 700, color: C.gold, lineHeight: 1.2, marginBottom: '20px' }}>Lerato</div>

        <div style={{ width: '40px', height: '1px', background: `${C.gold}40`, margin: '0 auto 16px' }} />

        <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>Saturday, April 15th 2026</div>
        <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>at 2:00 PM</div>
        <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginBottom: '20px' }}>Sandton Convention Centre</div>

        {/* RSVP button */}
        <div style={{
          padding: '10px 24px', borderRadius: '24px',
          background: `linear-gradient(135deg, ${C.gold}, #E8D59A)`,
          color: '#1a1020', fontWeight: 700, fontSize: '11px',
          display: 'inline-block', letterSpacing: '1px',
        }}>RSVP NOW</div>

        <div style={{ marginTop: '16px', fontSize: '9px', color: 'rgba(255,255,255,0.25)' }}>
          Kindly respond by March 30th
        </div>
      </div>

      {/* Powered by */}
      <div style={{ marginTop: '16px', fontSize: '9px', color: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', gap: '6px' }}>
        <img src="/gift-icon-v2.png" alt="GGR" style={{ height: '16px', opacity: 0.5 }} />
        <span>Powered by</span>
        <span style={{ color: C.coral, fontWeight: 700 }}>Grand Gather Registry</span>
      </div>
    </div>
  );
}

function ThankYouScreen() {
  return (
    <div style={{
      height: '100%',
      background: 'linear-gradient(180deg, #1a0f0f 0%, #2d1a1a 50%, #1a0f0f 100%)',
      fontFamily: "'Instrument Sans', sans-serif",
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: '20px',
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Gold shimmer decoration */}
      <div style={{ position: 'absolute', top: '15%', left: '10%', width: '80px', height: '80px', background: `radial-gradient(circle, ${C.gold}15 0%, transparent 70%)`, borderRadius: '50%' }} />
      <div style={{ position: 'absolute', bottom: '20%', right: '10%', width: '60px', height: '60px', background: `radial-gradient(circle, ${C.coral}10 0%, transparent 70%)`, borderRadius: '50%' }} />

      {/* Flower SVG decoration top */}
      <svg width="50" height="50" viewBox="0 0 50 50" style={{ position: 'absolute', top: '12%', right: '15%', opacity: 0.2 }}>
        <circle cx="25" cy="15" r="8" fill={C.coral} />
        <circle cx="15" cy="25" r="8" fill={C.coral} />
        <circle cx="35" cy="25" r="8" fill={C.coral} />
        <circle cx="25" cy="35" r="8" fill={C.coral} />
        <circle cx="25" cy="25" r="6" fill={C.gold} />
      </svg>

      <svg width="40" height="40" viewBox="0 0 40 40" style={{ position: 'absolute', bottom: '15%', left: '12%', opacity: 0.15 }}>
        <circle cx="20" cy="12" r="6" fill="#E8A0BF" />
        <circle cx="12" cy="20" r="6" fill="#E8A0BF" />
        <circle cx="28" cy="20" r="6" fill="#E8A0BF" />
        <circle cx="20" cy="28" r="6" fill="#E8A0BF" />
        <circle cx="20" cy="20" r="5" fill={C.gold} />
      </svg>

      <div style={{ textAlign: 'center', maxWidth: '240px' }}>
        <div style={{ fontSize: '10px', color: `${C.gold}80`, letterSpacing: '4px', textTransform: 'uppercase', marginBottom: '16px' }}>Thank You</div>

        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '22px', color: C.gold, fontWeight: 700, lineHeight: 1.3, marginBottom: '20px' }}>
          From Our Hearts to Yours
        </div>

        <div style={{ width: '40px', height: '1px', background: `${C.gold}40`, margin: '0 auto 20px' }} />

        <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', lineHeight: 1.8, marginBottom: '24px' }}>
          Dear Sipho, your presence at our wedding made the day truly special. Thank you for celebrating this beautiful milestone with us. Your love and blessings mean the world. Enkosi kakhulu! 💛
        </p>

        <div style={{ fontSize: '13px', fontStyle: 'italic', color: C.gold, marginBottom: '12px', fontFamily: "'Playfair Display', serif" }}>
          Thabo & Lerato
        </div>

        <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.25)', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '4px' }}>Celebrated</div>
        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', fontWeight: 600 }}>April 15, 2026</div>
      </div>
    </div>
  );
}

function GiftRegistryScreen() {
  return (
    <div style={{ height: '100%', background: '#fff', fontFamily: "'Instrument Sans', sans-serif", fontSize: '12px' }}>
      <div style={{ padding: '36px 14px 12px', background: `linear-gradient(135deg, ${C.gold}, #E8D59A)` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <span style={{ fontSize: '16px' }}>←</span>
          <span style={{ flex: 1, fontSize: '15px', fontWeight: 700, color: '#1a1020' }}>Gift Registry</span>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <div style={{ flex: 1, padding: '10px', borderRadius: '10px', background: 'rgba(255,255,255,0.3)', textAlign: 'center' }}>
            <div style={{ fontSize: '16px', fontWeight: 800, color: '#1a1020' }}>R 24,500</div>
            <div style={{ fontSize: '8px', color: '#1a102099', fontWeight: 600 }}>Total Received</div>
          </div>
          <div style={{ flex: 1, padding: '10px', borderRadius: '10px', background: 'rgba(255,255,255,0.3)', textAlign: 'center' }}>
            <div style={{ fontSize: '16px', fontWeight: 800, color: '#1a1020' }}>8/15</div>
            <div style={{ fontSize: '8px', color: '#1a102099', fontWeight: 600 }}>Gifts Claimed</div>
          </div>
        </div>
      </div>

      <div style={{ padding: '12px 14px' }}>
        <div style={{ fontSize: '11px', fontWeight: 700, color: '#333', marginBottom: '10px' }}>Gift Items</div>

        {[
          { name: 'KitchenAid Mixer', price: 'R 5,999', claimed: true, by: 'Sipho N.', code: 'GGR-KA-48291', icon: '🍳' },
          { name: 'Bedding Set (King)', price: 'R 2,499', claimed: true, by: 'Naledi M.', code: 'GGR-BD-73621', icon: '🛏️' },
          { name: 'Cash: Honeymoon Fund', price: 'R 8,000', claimed: true, by: '4 contributors', code: '', icon: '✈️' },
          { name: 'Dinner Set (12pc)', price: 'R 3,200', claimed: false, by: '', code: '', icon: '🍽️' },
          { name: 'Smart TV 55"', price: 'R 8,999', claimed: false, by: '', code: '', icon: '📺' },
          { name: 'Microwave', price: 'R 2,199', claimed: true, by: 'Bongani Z.', code: 'GGR-MW-55103', icon: '🔌' },
          { name: 'Cash: Home Deposit', price: 'R 15,000', claimed: false, by: '', code: '', icon: '🏠' },
        ].map((g, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            padding: '10px 0', borderBottom: '1px solid #f5f5f5',
          }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: g.claimed ? '#10B98110' : '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>{g.icon}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#1a1a1a' }}>{g.name}</div>
              <div style={{ fontSize: '9px', color: '#999' }}>
                {g.price}{g.claimed ? ` • by ${g.by}` : ''}
              </div>
              {g.code && <div style={{ fontSize: '8px', color: C.gold, fontWeight: 600, fontFamily: 'monospace' }}>{g.code}</div>}
            </div>
            <div style={{
              padding: '3px 8px', borderRadius: '20px', fontSize: '9px', fontWeight: 700,
              background: g.claimed ? '#10B98115' : '#F59E0B15',
              color: g.claimed ? '#10B981' : '#F59E0B',
            }}>{g.claimed ? 'Claimed' : 'Available'}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function VendorScreen() {
  return (
    <div style={{ height: '100%', background: '#FAFAFA', fontFamily: "'Instrument Sans', sans-serif", fontSize: '12px' }}>
      <div style={{ padding: '36px 14px 14px', background: 'white' }}>
        <div style={{ fontSize: '18px', fontWeight: 700, color: '#1a1a1a', marginBottom: '10px' }}>Vendor Marketplace</div>
        {/* Category pills */}
        <div style={{ display: 'flex', gap: '6px', overflowX: 'hidden', paddingBottom: '4px' }}>
          {['All', 'Catering', 'Photo', 'DJ', 'Decor', 'Venue'].map((cat, i) => (
            <div key={i} style={{
              padding: '6px 14px', borderRadius: '20px', fontSize: '10px', fontWeight: 600, whiteSpace: 'nowrap',
              background: i === 0 ? C.coral : '#f5f5f5',
              color: i === 0 ? 'white' : '#666',
            }}>{cat}</div>
          ))}
        </div>
      </div>

      <div style={{ padding: '10px 14px' }}>
        {[
          { name: 'Mama Zandi Catering', cat: 'Catering', loc: 'Soweto, GP', rating: 4.9, reviews: 128, price: 'From R180/head', color: '#F97316', avatar: 'MZ', verified: true },
          { name: 'Lebo Photography', cat: 'Photography', loc: 'Sandton, GP', rating: 4.8, reviews: 95, price: 'From R5,500', color: '#8B5CF6', avatar: 'LP', verified: true },
          { name: 'DJ Mkhize', cat: 'DJ & Sound', loc: 'Durban, KZN', rating: 4.7, reviews: 67, price: 'From R3,000', color: '#3B82F6', avatar: 'DM', verified: false },
          { name: 'Blossom Decor Studio', cat: 'Decor & Flowers', loc: 'Pretoria, GP', rating: 4.9, reviews: 82, price: 'From R8,000', color: '#EC4899', avatar: 'BD', verified: true },
          { name: 'Ubuntu Venue Hire', cat: 'Venue', loc: 'Centurion, GP', rating: 4.6, reviews: 43, price: 'From R12,000', color: '#10B981', avatar: 'UV', verified: true },
        ].map((v, i) => (
          <div key={i} style={{
            background: 'white', borderRadius: '14px', padding: '12px',
            marginBottom: '8px', boxShadow: '0 1px 6px rgba(0,0,0,0.04)',
            display: 'flex', gap: '10px',
          }}>
            <div style={{
              width: '48px', height: '48px', borderRadius: '12px',
              background: `${v.color}15`, color: v.color,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 800, fontSize: '14px', flexShrink: 0,
            }}>{v.avatar}</div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: '#1a1a1a' }}>{v.name}</span>
                {v.verified && <span style={{ fontSize: '10px', color: '#3B82F6' }}>✓</span>}
              </div>
              <div style={{ fontSize: '9px', color: '#999', marginTop: '2px' }}>{v.cat} • {v.loc}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                  <span style={{ fontSize: '10px', color: '#F59E0B' }}>★</span>
                  <span style={{ fontSize: '10px', fontWeight: 700, color: '#333' }}>{v.rating}</span>
                  <span style={{ fontSize: '9px', color: '#999' }}>({v.reviews})</span>
                </div>
                <span style={{ fontSize: '9px', color: C.coral, fontWeight: 600 }}>{v.price}</span>
              </div>
            </div>
            <div style={{
              alignSelf: 'center',
              padding: '6px 10px', borderRadius: '8px', fontSize: '9px',
              background: `${C.coral}12`, color: C.coral, fontWeight: 700,
            }}>Quote</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TicketScreen() {
  return (
    <div style={{
      height: '100%',
      background: 'linear-gradient(180deg, #E8A0BF 0%, #D4739D 100%)',
      fontFamily: "'Instrument Sans', sans-serif",
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: '20px',
    }}>
      <div style={{
        width: '100%', maxWidth: '240px',
        background: 'white',
        borderRadius: '20px',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
        position: 'relative',
      }}>
        {/* Top */}
        <div style={{ padding: '20px 16px', textAlign: 'center' }}>
          <div style={{ fontSize: '9px', color: '#999', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '8px' }}>Event Ticket</div>
          <div style={{ fontSize: '18px', fontWeight: 800, color: '#1a1a1a', marginBottom: '4px' }}>Thabo & Lerato</div>
          <div style={{ fontSize: '11px', color: '#D4739D', fontWeight: 600 }}>Wedding Celebration</div>
        </div>

        {/* Divider with circles */}
        <div style={{ position: 'relative', height: '1px', background: 'repeating-linear-gradient(90deg, #ddd 0px, #ddd 4px, transparent 4px, transparent 8px)' }}>
          <div style={{ position: 'absolute', left: '-10px', top: '-10px', width: '20px', height: '20px', borderRadius: '50%', background: 'linear-gradient(180deg, #E8A0BF, #D4739D)' }} />
          <div style={{ position: 'absolute', right: '-10px', top: '-10px', width: '20px', height: '20px', borderRadius: '50%', background: 'linear-gradient(180deg, #E8A0BF, #D4739D)' }} />
        </div>

        {/* Details */}
        <div style={{ padding: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
            {[
              { l: 'Date', v: 'Apr 15, 2026' },
              { l: 'Time', v: '14:00' },
              { l: 'Venue', v: 'Sandton CC' },
              { l: 'Guest', v: 'Sipho Ndlovu' },
            ].map((d, i) => (
              <div key={i}>
                <div style={{ fontSize: '8px', color: '#bbb', fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase' }}>{d.l}</div>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#333', marginTop: '2px' }}>{d.v}</div>
              </div>
            ))}
          </div>

          {/* QR Code mockup */}
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '100px', height: '100px', margin: '0 auto 8px',
              background: '#1a1a1a', borderRadius: '8px', padding: '8px',
              display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gridTemplateRows: 'repeat(8, 1fr)', gap: '2px',
            }}>
              {Array.from({ length: 64 }).map((_, i) => (
                <div key={i} style={{
                  background: [0,1,2,5,6,7,8,15,16,23,24,31,32,39,40,47,48,55,56,57,58,61,62,63,
                    3,4,9,10,14,17,18,22,25,26,30,33,34,38,41,42,46,49,50,54,59,60,
                    11,12,19,20,27,28,35,36,43,44,51,52].includes(i) ? 'white' : 'transparent',
                  borderRadius: '1px',
                }} />
              ))}
            </div>
            <div style={{ fontSize: '10px', fontWeight: 700, color: '#D4739D', fontFamily: 'monospace' }}>TKT-WED-2026-0042</div>
            <div style={{ fontSize: '8px', color: '#bbb', marginTop: '4px' }}>Scan at entrance</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function EventWallScreen() {
  return (
    <div style={{ height: '100%', background: '#fff', fontFamily: "'Instrument Sans', sans-serif", fontSize: '12px' }}>
      <div style={{ padding: '36px 14px 12px', background: 'white', borderBottom: '1px solid #f0f0f0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '16px' }}>←</span>
          <span style={{ flex: 1, fontSize: '15px', fontWeight: 700, color: '#1a1a1a' }}>Event Wall</span>
          <span style={{ fontSize: '10px', color: C.coral, fontWeight: 600 }}>+ Post</span>
        </div>
      </div>

      <div style={{ padding: '12px 14px', overflowY: 'hidden' }}>
        {/* Host announcement */}
        <div style={{ background: `${C.coral}08`, borderRadius: '14px', padding: '12px', marginBottom: '10px', border: `1px solid ${C.coral}15` }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
            <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: `linear-gradient(135deg, ${C.coral}, ${C.gold})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: '11px' }}>T</div>
            <div>
              <span style={{ fontSize: '11px', fontWeight: 700, color: '#333' }}>Thabo M.</span>
              <span style={{ fontSize: '9px', color: C.coral, marginLeft: '4px', fontWeight: 600 }}>Host</span>
              <div style={{ fontSize: '8px', color: '#999' }}>2 hours ago</div>
            </div>
          </div>
          <p style={{ fontSize: '11px', color: '#555', lineHeight: 1.6 }}>We're so excited for this Saturday! 🎉 Remember parking is available at the basement level. See you all there! 💒</p>
          <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
            <span style={{ fontSize: '10px', color: '#999' }}>❤️ 23</span>
            <span style={{ fontSize: '10px', color: '#999' }}>💬 8</span>
          </div>
        </div>

        {/* Photo post */}
        <div style={{ background: '#fafafa', borderRadius: '14px', padding: '12px', marginBottom: '10px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
            <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#8B5CF618', color: '#8B5CF6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '11px' }}>N</div>
            <div>
              <span style={{ fontSize: '11px', fontWeight: 700, color: '#333' }}>Naledi M.</span>
              <div style={{ fontSize: '8px', color: '#999' }}>5 hours ago</div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', borderRadius: '10px', overflow: 'hidden', marginBottom: '8px' }}>
            <div style={{ height: '60px', background: 'linear-gradient(135deg, #FFE4EC, #FFB6C1)' }} />
            <div style={{ height: '60px', background: 'linear-gradient(135deg, #E8D5B7, #D4A574)' }} />
          </div>
          <p style={{ fontSize: '11px', color: '#555', lineHeight: 1.5 }}>Look what I found for the couple! 🎁 Can't wait! #ThaboAndLerato</p>
          <div style={{ display: 'flex', gap: '12px', marginTop: '6px' }}>
            <span style={{ fontSize: '10px', color: '#999' }}>❤️ 15</span>
            <span style={{ fontSize: '10px', color: '#999' }}>💬 3</span>
          </div>
        </div>

        {/* Another post */}
        <div style={{ background: '#fafafa', borderRadius: '14px', padding: '12px', marginBottom: '10px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
            <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#10B98118', color: '#10B981', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '11px' }}>B</div>
            <div>
              <span style={{ fontSize: '11px', fontWeight: 700, color: '#333' }}>Bongani Z.</span>
              <div style={{ fontSize: '8px', color: '#999' }}>Yesterday</div>
            </div>
          </div>
          <p style={{ fontSize: '11px', color: '#555', lineHeight: 1.5 }}>Who else is getting a new outfit for this? 😂 This wedding is going to be amazing! Congratulations to the beautiful couple! 💍✨</p>
          <div style={{ display: 'flex', gap: '12px', marginTop: '6px' }}>
            <span style={{ fontSize: '10px', color: '#999' }}>❤️ 31</span>
            <span style={{ fontSize: '10px', color: '#999' }}>💬 12</span>
            <span style={{ fontSize: '10px', color: '#999' }}>🔥 5</span>
          </div>
        </div>
      </div>
    </div>
  );
}


function GalleryScreen() {
  return (
    <div style={{ height: '100%', background: '#fff', fontFamily: "'Instrument Sans', sans-serif", fontSize: '12px' }}>
      {/* Header */}
      <div style={{ padding: '36px 14px 12px', background: 'white', borderBottom: '1px solid #f0f0f0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <span style={{ fontSize: '16px' }}>←</span>
          <span style={{ flex: 1, fontSize: '15px', fontWeight: 700, color: '#1a1a1a' }}>Gallery</span>
          <span style={{ fontSize: '10px', color: C.coral, fontWeight: 600 }}>+ Upload</span>
        </div>
        {/* Layout toggles */}
        <div style={{ display: 'flex', gap: '6px' }}>
          {['Grid', 'Masonry', 'Carousel'].map((l, i) => (
            <div key={i} style={{
              padding: '5px 12px', borderRadius: '20px', fontSize: '10px', fontWeight: 600,
              background: i === 0 ? C.coral : '#f5f5f5',
              color: i === 0 ? 'white' : '#888',
            }}>{l}</div>
          ))}
        </div>
      </div>

      {/* Photo grid */}
      <div style={{ padding: '10px' }}>
        {/* Stats bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 4px 10px', fontSize: '10px', color: '#999' }}>
          <span>24 photos • 3 videos</span>
          <span style={{ color: C.coral, fontWeight: 600 }}>Select All</span>
        </div>

        {/* Row 1 - big + small */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '4px', marginBottom: '4px' }}>
          <div style={{
            height: '140px', borderRadius: '10px',
            background: 'linear-gradient(135deg, #FFE4EC 0%, #E8A0BF 50%, #D4739D 100%)',
            position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ position: 'absolute', bottom: '8px', left: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{ padding: '3px 8px', borderRadius: '12px', background: 'rgba(0,0,0,0.5)', fontSize: '8px', color: 'white', fontWeight: 600 }}>❤️ 45</div>
            </div>
            {/* Simulated couple silhouette */}
            <div style={{ position: 'absolute', bottom: '20px', right: '20px', fontSize: '36px', opacity: 0.3 }}>💒</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ flex: 1, borderRadius: '10px', background: 'linear-gradient(135deg, #E8D5B7, #C9A84C)', position: 'relative' }}>
              <div style={{ position: 'absolute', bottom: '4px', left: '6px', fontSize: '7px', color: 'white', fontWeight: 600, background: 'rgba(0,0,0,0.4)', padding: '2px 6px', borderRadius: '8px' }}>❤️ 32</div>
            </div>
            <div style={{ flex: 1, borderRadius: '10px', background: 'linear-gradient(135deg, #C8E6C9, #81C784)', position: 'relative' }}>
              <div style={{ position: 'absolute', bottom: '4px', left: '6px', fontSize: '7px', color: 'white', fontWeight: 600, background: 'rgba(0,0,0,0.4)', padding: '2px 6px', borderRadius: '8px' }}>❤️ 28</div>
            </div>
          </div>
        </div>

        {/* Row 2 - 3 equal */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px', marginBottom: '4px' }}>
          <div style={{ height: '90px', borderRadius: '10px', background: 'linear-gradient(135deg, #BBDEFB, #64B5F6)', position: 'relative' }}>
            <div style={{ position: 'absolute', top: '6px', right: '6px', padding: '2px 6px', borderRadius: '8px', background: 'rgba(0,0,0,0.4)', fontSize: '7px', color: 'white' }}>🎥 0:42</div>
          </div>
          <div style={{ height: '90px', borderRadius: '10px', background: 'linear-gradient(135deg, #F3E5F5, #CE93D8)' }} />
          <div style={{ height: '90px', borderRadius: '10px', background: 'linear-gradient(135deg, #FFF3E0, #FFB74D)' }} />
        </div>

        {/* Row 3 - small + big */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '4px', marginBottom: '4px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ flex: 1, borderRadius: '10px', background: 'linear-gradient(135deg, #FFCDD2, #EF9A9A)' }} />
            <div style={{ flex: 1, borderRadius: '10px', background: 'linear-gradient(135deg, #D7CCC8, #A1887F)' }} />
          </div>
          <div style={{
            height: '130px', borderRadius: '10px',
            background: 'linear-gradient(135deg, #1a1020 0%, #2a1f3a 50%, #D4739D 100%)',
            position: 'relative',
          }}>
            <div style={{ position: 'absolute', top: '6px', right: '6px', padding: '2px 6px', borderRadius: '8px', background: 'rgba(0,0,0,0.4)', fontSize: '7px', color: 'white' }}>🎥 2:15</div>
            <div style={{ position: 'absolute', bottom: '8px', left: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{ padding: '3px 8px', borderRadius: '12px', background: 'rgba(0,0,0,0.5)', fontSize: '8px', color: 'white', fontWeight: 600 }}>❤️ 67</div>
              <div style={{ padding: '3px 8px', borderRadius: '12px', background: 'rgba(0,0,0,0.5)', fontSize: '8px', color: 'white', fontWeight: 600 }}>💬 12</div>
            </div>
          </div>
        </div>

        {/* Row 4 - 3 equal */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px' }}>
          <div style={{ height: '90px', borderRadius: '10px', background: 'linear-gradient(135deg, #B2DFDB, #4DB6AC)' }} />
          <div style={{ height: '90px', borderRadius: '10px', background: 'linear-gradient(135deg, #FFE082, #FFC107)', position: 'relative' }}>
            <div style={{ position: 'absolute', bottom: '4px', left: '6px', fontSize: '7px', color: 'white', fontWeight: 600, background: 'rgba(0,0,0,0.4)', padding: '2px 6px', borderRadius: '8px' }}>❤️ 19</div>
          </div>
          <div style={{ height: '90px', borderRadius: '10px', background: 'linear-gradient(135deg, #F8BBD0, #EC407A)' }} />
        </div>
      </div>
    </div>
  );
}

function InvitationManagerScreen() {
  return (
    <div style={{ height: '100%', background: '#fff', fontFamily: "'Instrument Sans', sans-serif", fontSize: '12px' }}>
      {/* Header */}
      <div style={{ padding: '36px 14px 12px', background: 'linear-gradient(135deg, #E8A0BF 0%, #D4739D 100%)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <span style={{ color: 'white', fontSize: '16px' }}>←</span>
          <span style={{ flex: 1, fontSize: '15px', fontWeight: 700, color: 'white' }}>Invitations</span>
          <span style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '20px', padding: '4px 10px', fontSize: '10px', color: 'white', fontWeight: 600 }}>120 sent</span>
        </div>
        {/* Send stats */}
        <div style={{ display: 'flex', gap: '6px' }}>
          {[
            { n: '96', l: 'Delivered', c: '#10B981', bg: 'rgba(16,185,129,0.15)' },
            { n: '78', l: 'Opened', c: '#3B82F6', bg: 'rgba(59,130,246,0.15)' },
            { n: '24', l: 'Pending', c: '#F59E0B', bg: 'rgba(245,158,11,0.15)' },
          ].map((s, i) => (
            <div key={i} style={{ flex: 1, padding: '8px', borderRadius: '10px', background: s.bg, textAlign: 'center' }}>
              <div style={{ fontSize: '15px', fontWeight: 800, color: s.c }}>{s.n}</div>
              <div style={{ fontSize: '8px', color: s.c, fontWeight: 600, opacity: 0.8 }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ padding: '12px 14px' }}>
        {/* Theme selector */}
        <div style={{ marginBottom: '14px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#333', marginBottom: '8px' }}>Invitation Theme</div>
          <div style={{ display: 'flex', gap: '8px', overflowX: 'hidden' }}>
            {[
              { name: 'Elegant', bg: 'linear-gradient(135deg, #1a1020, #2a1f3a)', border: '#C9A84C', active: true },
              { name: 'Romantic', bg: 'linear-gradient(135deg, #FFE4EC, #E8A0BF)', border: '#E8A0BF', active: false },
              { name: 'Rustic', bg: 'linear-gradient(135deg, #E8D5B7, #D4A574)', border: '#D4A574', active: false },
              { name: 'Modern', bg: 'linear-gradient(135deg, #f5f5f5, #e0e0e0)', border: '#999', active: false },
            ].map((t, i) => (
              <div key={i} style={{
                width: '56px', textAlign: 'center', flexShrink: 0,
              }}>
                <div style={{
                  width: '52px', height: '68px', borderRadius: '8px',
                  background: t.bg, border: t.active ? `2px solid ${C.coral}` : '2px solid transparent',
                  boxShadow: t.active ? `0 0 0 2px ${C.coral}40` : 'none',
                  marginBottom: '4px',
                  position: 'relative', overflow: 'hidden',
                }}>
                  {/* Mini card lines */}
                  <div style={{ position: 'absolute', top: '12px', left: '50%', transform: 'translateX(-50%)', width: '24px', height: '2px', background: t.border, borderRadius: '1px', opacity: 0.6 }} />
                  <div style={{ position: 'absolute', top: '20px', left: '50%', transform: 'translateX(-50%)', width: '16px', height: '1px', background: t.border, borderRadius: '1px', opacity: 0.3 }} />
                  <div style={{ position: 'absolute', top: '30px', left: '50%', transform: 'translateX(-50%)', width: '20px', height: '6px', background: t.border, borderRadius: '3px', opacity: 0.2 }} />
                </div>
                <div style={{ fontSize: '9px', fontWeight: 600, color: t.active ? C.coral : '#999' }}>{t.name}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Invitation preview */}
        <div style={{ marginBottom: '14px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#333', marginBottom: '8px' }}>Preview</div>
          <div style={{
            borderRadius: '14px', overflow: 'hidden',
            background: 'linear-gradient(180deg, #2a1f3a 0%, #1a1020 100%)',
            border: `1px solid ${C.gold}30`,
            padding: '20px 16px', textAlign: 'center',
          }}>
            <img src="/gift-icon-v2.png" alt="GGR" style={{ height: '20px', marginBottom: '8px', opacity: 0.6 }} />
            <div style={{ fontSize: '8px', color: `${C.gold}80`, letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '8px' }}>You are invited</div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '16px', color: C.gold, fontWeight: 700, lineHeight: 1.3 }}>
              Thabo & Lerato
            </div>
            <div style={{ fontSize: '8px', color: 'rgba(255,255,255,0.4)', margin: '6px 0' }}>Saturday, April 15th 2026 • 2:00 PM</div>
            <div style={{ display: 'inline-block', padding: '5px 14px', borderRadius: '20px', background: `linear-gradient(135deg, ${C.gold}, #E8D59A)`, fontSize: '8px', fontWeight: 700, color: '#1a1020' }}>RSVP NOW</div>
          </div>
        </div>

        {/* Send options */}
        <div style={{ marginBottom: '14px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#333', marginBottom: '8px' }}>Send via</div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {[
              { icon: '💬', label: 'WhatsApp', color: '#25D366' },
              { icon: '✉️', label: 'Email', color: '#3B82F6' },
              { icon: '🔗', label: 'Copy Link', color: '#8B5CF6' },
            ].map((s, i) => (
              <div key={i} style={{
                flex: 1, padding: '12px 8px', borderRadius: '12px', textAlign: 'center',
                background: `${s.color}10`, border: `1px solid ${s.color}20`,
              }}>
                <div style={{ fontSize: '20px', marginBottom: '4px' }}>{s.icon}</div>
                <div style={{ fontSize: '9px', fontWeight: 700, color: s.color }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Guest send status */}
        <div>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#333', marginBottom: '8px' }}>Recent Sends</div>
          {[
            { name: 'Sipho Ndlovu', status: 'Opened', time: '2m ago', color: '#3B82F6' },
            { name: 'Naledi Molefe', status: 'Delivered', time: '5m ago', color: '#10B981' },
            { name: 'Bongani Zulu', status: 'Opened', time: '12m ago', color: '#3B82F6' },
            { name: 'Palesa Khumalo', status: 'Pending', time: '1h ago', color: '#F59E0B' },
          ].map((g, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '8px 0', borderBottom: '1px solid #f5f5f5',
            }}>
              <div style={{
                width: '28px', height: '28px', borderRadius: '50%',
                background: `${g.color}15`, color: g.color,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: '11px',
              }}>{g.name[0]}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: '#333' }}>{g.name}</div>
                <div style={{ fontSize: '8px', color: '#bbb' }}>via WhatsApp • {g.time}</div>
              </div>
              <div style={{
                padding: '2px 8px', borderRadius: '12px', fontSize: '8px', fontWeight: 700,
                background: `${g.color}12`, color: g.color,
              }}>{g.status}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════
   MAIN SHOWCASE PAGE
   ═══════════════════════════════════════════ */
export default function AppShowcase() {
  return (
    <div style={{
      background: C.charcoal, minHeight: '100vh', overflow: 'hidden',
      fontFamily: "'Instrument Sans', sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;0,800;1,400;1,600&family=Instrument+Sans:wght@400;500;600;700&display=swap');
        .showcase-page * { box-sizing: border-box; margin: 0; padding: 0; }
        .showcase-section { min-height: 100vh; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 60px 24px; position: relative; overflow: hidden; }
        .phones-row { display: flex; gap: 32px; flex-wrap: wrap; justify-content: center; align-items: flex-start; }
        @media (max-width: 768px) { .phones-row { gap: 24px; } }
        .print-float { position: fixed; top: 20px; right: 20px; z-index: 100; }
        .print-float button { padding: 10px 18px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.15); background: rgba(0,0,0,0.7); backdrop-filter: blur(12px); color: white; font-size: 13px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 6px; font-family: 'Instrument Sans', sans-serif; transition: all 0.2s; }
        .print-float button:hover { background: rgba(255,255,255,0.1); }
        @media print {
          .print-float { display: none !important; }
          .showcase-section { page-break-after: always; min-height: auto; padding: 40px 20px; }
        }
        @keyframes floatSlow { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-15px); } }
      `}</style>

      <div className="showcase-page">

        {/* Print button */}
        <div className="print-float">
          <button onClick={() => window.print()}>🖨️ Print / Save PDF</button>
        </div>

        {/* ══════════════════════════════════════
            SECTION 1: HERO + DASHBOARD
            ══════════════════════════════════════ */}
        <section className="showcase-section" style={{
          background: `radial-gradient(ellipse at 30% 40%, rgba(227,66,92,0.08) 0%, transparent 50%), radial-gradient(ellipse at 70% 60%, rgba(201,168,76,0.06) 0%, transparent 50%), ${C.charcoal}`,
        }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '20px' }}>
                <img src="/gift-icon-v2.png" alt="GGR" style={{ height: '48px', filter: 'drop-shadow(0 4px 12px rgba(227,66,92,0.3))' }} />
                <span style={{ fontFamily: "'Playfair Display', serif", fontSize: '24px', fontWeight: 700, color: 'white' }}>Grand Gather Registry</span>
              </div>
              <h1 style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(32px, 5vw, 56px)', fontWeight: 700,
                color: 'white', lineHeight: 1.15, marginBottom: '12px',
              }}>
                The App in <span style={{ color: C.coral, fontStyle: 'italic' }}>Action</span>
              </h1>
              <p style={{ fontSize: '16px', color: 'rgba(255,255,255,0.4)', maxWidth: '500px', margin: '0 auto' }}>
                See how South Africa's smartest event planning platform works, screen by screen.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="phones-row">
              <Phone label="Dashboard">
                <DashboardScreen />
              </Phone>
              <Phone label="Event Workspace" style={{ marginTop: '40px' }}>
                <EventWorkspaceScreen />
              </Phone>
            </div>
          </Reveal>
        </section>

        {/* ══════════════════════════════════════
            SECTION 2: GUESTS & GIGI
            ══════════════════════════════════════ */}
        <section className="showcase-section" style={{
          background: `linear-gradient(180deg, ${C.charcoalMid} 0%, ${C.charcoal} 100%)`,
        }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <span style={{ fontSize: '12px', fontWeight: 700, color: C.gold, letterSpacing: '3px', textTransform: 'uppercase' }}>Smart Guest Management & AI</span>
              <h2 style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 700,
                color: 'white', lineHeight: 1.15, marginTop: '14px',
              }}>
                Track Guests. <span style={{ color: C.coral }}>Talk to GiGi.</span>
              </h2>
              <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.35)', maxWidth: '450px', margin: '12px auto 0' }}>
                Real-time RSVP tracking and an AI assistant that speaks 7 South African languages.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="phones-row">
              <Phone label="Guest List & RSVPs">
                <GuestListScreen />
              </Phone>
              <Phone label="GiGi AI Assistant" style={{ marginTop: '40px' }}>
                <GigiScreen />
              </Phone>
            </div>
          </Reveal>
        </section>

        {/* ══════════════════════════════════════
            SECTION 3: INVITATIONS & THANK YOU
            ══════════════════════════════════════ */}
        <section className="showcase-section" style={{
          background: `radial-gradient(ellipse at 40% 50%, rgba(201,168,76,0.06) 0%, transparent 50%), ${C.charcoal}`,
        }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <span style={{ fontSize: '12px', fontWeight: 700, color: C.coral, letterSpacing: '3px', textTransform: 'uppercase' }}>Beautiful Communications</span>
              <h2 style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 700,
                color: 'white', lineHeight: 1.15, marginTop: '14px',
              }}>
                Invite with <span style={{ color: C.gold }}>Elegance</span>
              </h2>
              <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.35)', maxWidth: '450px', margin: '12px auto 0' }}>
                Stunning digital invitations and personalised thank you cards with 10 beautiful styles.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="phones-row">
              <Phone label="Digital Invitation">
                <InvitationScreen />
              </Phone>
              <Phone label="Thank You Card" style={{ marginTop: '40px' }}>
                <ThankYouScreen />
              </Phone>
            </div>
          </Reveal>
        </section>

        {/* ══════════════════════════════════════
            SECTION 4: GIFTS, VENDORS, TICKETS
            ══════════════════════════════════════ */}
        <section className="showcase-section" style={{
          background: `linear-gradient(180deg, ${C.charcoalMid} 0%, ${C.charcoal} 100%)`,
          paddingBottom: '80px',
        }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <span style={{ fontSize: '12px', fontWeight: 700, color: '#10B981', letterSpacing: '3px', textTransform: 'uppercase' }}>Complete Ecosystem</span>
              <h2 style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 700,
                color: 'white', lineHeight: 1.15, marginTop: '14px',
              }}>
                Gifts. Vendors. <span style={{ color: C.coral }}>Tickets.</span>
              </h2>
              <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.35)', maxWidth: '500px', margin: '12px auto 0' }}>
                A full ecosystem — gift registry with tracking, verified vendor marketplace, and digital QR tickets.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="phones-row">
              <Phone label="Gift Registry">
                <GiftRegistryScreen />
              </Phone>
              <Phone label="Vendor Marketplace" style={{ marginTop: '40px' }}>
                <VendorScreen />
              </Phone>
              <Phone label="QR Event Ticket">
                <TicketScreen />
              </Phone>
            </div>
          </Reveal>
        </section>

        {/* ══════════════════════════════════════
            SECTION 5: EVENT WALL
            ══════════════════════════════════════ */}
        <section className="showcase-section" style={{
          background: `radial-gradient(ellipse at 60% 40%, rgba(227,66,92,0.06) 0%, transparent 50%), ${C.charcoal}`,
          paddingBottom: '80px',
        }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <span style={{ fontSize: '12px', fontWeight: 700, color: '#EC4899', letterSpacing: '3px', textTransform: 'uppercase' }}>Social Features</span>
              <h2 style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 700,
                color: 'white', lineHeight: 1.15, marginTop: '14px',
              }}>
                The <span style={{ color: C.coral }}>Event Wall</span>
              </h2>
              <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.35)', maxWidth: '450px', margin: '12px auto 0' }}>
                An interactive social wall where guests share excitement, photos, and celebrate together.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <Phone label="Interactive Event Wall">
              <EventWallScreen />
            </Phone>
          </Reveal>
        </section>

        {/* ══════════════════════════════════════
            SECTION 6: GALLERY & INVITATION MANAGER
            ══════════════════════════════════════ */}
        <section className="showcase-section" style={{
          background: `radial-gradient(ellipse at 50% 30%, rgba(201,168,76,0.06) 0%, transparent 50%), linear-gradient(180deg, ${C.charcoal} 0%, ${C.charcoalMid} 100%)`,
        }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <span style={{ fontSize: '12px', fontWeight: 700, color: C.gold, letterSpacing: '3px', textTransform: 'uppercase' }}>Capture & Connect</span>
              <h2 style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 700,
                color: 'white', lineHeight: 1.15, marginTop: '14px',
              }}>
                Gallery & <span style={{ color: C.coral }}>Digital Invitations</span>
              </h2>
              <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.35)', maxWidth: '500px', margin: '12px auto 0' }}>
                A stunning shared photo gallery with multiple layouts, and a powerful invitation manager to send, track, and manage your digital invites.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="phones-row">
              <Phone label="Photo & Video Gallery">
                <GalleryScreen />
              </Phone>
              <Phone label="Invitation Manager" style={{ marginTop: '40px' }}>
                <InvitationManagerScreen />
              </Phone>
            </div>
          </Reveal>
        </section>

        {/* ══════════════════════════════════════
            FOOTER
            ══════════════════════════════════════ */}
        <section style={{
          padding: '60px 24px', textAlign: 'center',
          background: C.charcoal,
          borderTop: '1px solid rgba(255,255,255,0.04)',
        }}>
          <Reveal>
            <img src="/gift-icon-v2.png" alt="GGR" style={{ height: '56px', marginBottom: '16px', opacity: 0.7 }} />
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '22px', color: 'white', fontWeight: 600, marginBottom: '4px' }}>Grand Gather Registry</div>
            <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.3)', marginBottom: '8px' }}>Plan. Register. Celebrate.</div>
            <div style={{ fontSize: '12px', color: C.coral, fontWeight: 600 }}>grandgatherregistry.co.za</div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.2)', marginTop: '6px' }}>info@grandgatherregistry.co.za</div>
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.1)', marginTop: '16px' }}>Made with ♥ · South Africa</div>
          </Reveal>
        </section>

      </div>
    </div>
  );
}
